import { useState, useMemo, useRef, useCallback } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    Tooltip,
  } from '@mui/material';
  import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
  import { useDateSort } from '../../../../hooks/useDateSort';
  import TableActionsMenu from '../../../../components/common/TableActionsMenu';
  import { AdpopcornTotalMetrics } from '../../../../types/adpopcorn.types';
  import type { Dayjs } from 'dayjs';

type Props = {
  data: AdpopcornTotalMetrics;
  selectedApp?: string | null;
  startDate: Dayjs;
  endDate: Dayjs;
};

export default function AdpopcornTotalMetricsDetailTable({ data, selectedApp, startDate, endDate }: Props) {
    const dailyStats = data.dailyStats;
    const rowsPerPage = 31;
    const [page, setPage] = useState(0);
    
    // 테이블 ref - 엑셀 다운로드용
    const tableRef = useRef<HTMLTableElement>(null);

    const { sorted: sortedData, sortDirection, toggleSort } = useDateSort(dailyStats);

    // 파일명 생성
    const filename = useMemo(() => {
      const appPart = selectedApp && selectedApp !== '앱 전체' ? `_${selectedApp}` : '_전체';
      return `${startDate.format('YYMMDD')}-${endDate.format('YYMMDD')}${appPart}`;
    }, [startDate, endDate, selectedApp]);

    // baseSorted - 내림차순: [최신날짜, ..., 오래된날짜]
    // 정렬 방향과 무관하게 baseSorted 기준으로 증감률 계산
    const baseSorted = useMemo(() => {
        const copied = [...dailyStats];
        copied.sort((a, b) => b.date.localeCompare(a.date)); // 내림차순
        return copied;
    }, [dailyStats]);

    const paged = useMemo(
        () => sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [sortedData, page]
    );

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(value);
    };
    // 숫자만 콤마로 표시 (엑셀 내보내기용)
    const formatNumberOnly = (value: number) => {
        return Math.round(value).toLocaleString('ko-KR');
    };

    const calculateRevenueDelta = (currentRow: typeof sortedData[0]) => {
        // baseSorted에서 현재 날짜의 인덱스 찾기
        const currentIndex = baseSorted.findIndex(item => item.date === currentRow.date);
        
        // baseSorted는 항상 내림차순이므로 idx + 1이 전날 (더 오래된 날짜)
        if (currentIndex === -1 || currentIndex === baseSorted.length - 1) {
            return '-';
        }

        const prevRow = baseSorted[currentIndex + 1];
        if (!prevRow) {
            return '-';
        }

        // 현재 날짜 - 전날 매출
        const delta = currentRow.revenue - prevRow.revenue;
        const rawDeltaPercentage = prevRow.revenue > 0 ? Math.round((delta / prevRow.revenue) * 100) : 0;
        const deltaPercentage = rawDeltaPercentage.toLocaleString('ko-KR');

        const isPositive = delta >= 0;
        const arrow = isPositive ? '▲' : '▼';
        const sign = isPositive ? '+' : '';

        return (
            <Box
                component="span"
                sx={{
                    color: isPositive ? '#D32F2F' : '#1976D2',
                    display: 'inline-flex',
                    alignItems: 'center',
                }}
            >
                {arrow} {deltaPercentage}% ({sign}{formatCurrency(delta)})
            </Box>
        );
    };

  const calculateFillRate = (req: number, imp: number) => {
    return req > 0 ? ((imp / req) * 100).toFixed(2) : '0.00';
    };

    const calculateCTR = (imp: number, clk: number) => {
        return imp > 0 ? ((clk / imp) * 100).toFixed(2) : '0.00';
    };

    const calculateECPM = (revenue: number, imp: number) => {
        return imp > 0 ? formatCurrency((revenue / imp) * 1000) : formatCurrency(0);
    };

    // 순수익 계산: (총매출 × 0.85) - (cash / 6), 소수점 버림
  const calculateNetProfit = useCallback((row: typeof sortedData[0]) => {
    return Math.floor((row.revenue * 0.85) - (row.cash / 6));
  }, []);

    // 합계 계산 (평균: fill rate/CTR/eCPM, 합계: 총 매출/순수익/request/response/impression/click)
    const totals = useMemo(() => {
        const totalRevenue = sortedData.reduce((sum, row) => sum + row.revenue, 0);
        // 순수익 계산: (총매출 × 0.85) - (cash / 6), 소수점 버림
        const totalProfit = sortedData.reduce((sum, row) => {
            return sum + Math.floor((row.revenue * 0.85) - (row.cash / 6));
        }, 0);
        const totalReq = sortedData.reduce((sum, row) => sum + row.req, 0);
        const totalRes = sortedData.reduce((sum, row) => sum + row.res, 0);
        const totalImp = sortedData.reduce((sum, row) => sum + row.imp, 0);
        const totalClk = sortedData.reduce((sum, row) => sum + row.clk, 0);
        
    // 행별 비율을 산술평균으로 계산 (유효 값만 포함)
    const average = (vals: Array<number | undefined>) => {
      const nums = vals.filter((v): v is number => v !== undefined);
      return nums.length ? nums.reduce((s, v) => s + v, 0) / nums.length : 0;
    };

    const avgFillRate = average(
      sortedData.map((row) =>
        row.req > 0 ? (row.imp / row.req) * 100 : undefined
      )
    ).toFixed(2);

    const avgCTR = average(
      sortedData.map((row) =>
        row.imp > 0 && row.clk <= row.imp ? (row.clk / row.imp) * 100 : undefined
      )
    ).toFixed(2);

    const avgECPM = formatCurrency(
      average(
        sortedData.map((row) =>
          row.imp > 0 ? (row.revenue / row.imp) * 1000 : undefined
        )
      )
    );
        
        return {
            totalRevenue,
            totalProfit,
            totalReq,
            totalRes,
            totalImp,
            totalClk,
            avgFillRate,
            avgCTR,
            avgECPM
        };
    }, [sortedData]);

    // 엑셀 다운로드용 전체 데이터 (현재 정렬 상태 그대로, 페이지네이션 무시)
    const allRowsDataForExcel = useMemo(() => {
        return sortedData.map((row) => {
            const currentIndex = baseSorted.findIndex(item => item.date === row.date);
            const prevRow = currentIndex !== -1 && currentIndex < baseSorted.length - 1 
                ? baseSorted[currentIndex + 1] 
                : null;
            
            // 증감률 텍스트 계산 (부호 + %는 문자열로 유지)
            let revenueDeltaText = '-';
            if (prevRow) {
                const delta = row.revenue - prevRow.revenue;
                const rawDeltaPercentage = prevRow.revenue > 0 ? Math.round((delta / prevRow.revenue) * 100) : 0;
                const arrow = delta >= 0 ? '▲' : '▼';
                const deltaPercentageAbs = Math.abs(rawDeltaPercentage).toLocaleString('ko-KR');
                const deltaAmountAbs = Math.abs(delta).toLocaleString('ko-KR');
                const sign = delta >= 0 ? '+' : '-';
                revenueDeltaText = `${arrow} ${deltaPercentageAbs}% (${sign}${deltaAmountAbs})`;
            }

            // 퍼센트 컬럼은 소수로 전달 (0.xx 형태, excelExport.ts에서 % 포맷이 자동으로 100 곱함)
            // 예: 0.1523 → 엑셀에서 15.23%로 표시
            const fillRate = row.req > 0 ? row.imp / row.req : 0;
            const ctr = row.imp > 0 ? row.clk / row.imp : 0;

            // 순수익 (TODO: 계산식 추가 필요)
            const netProfit = calculateNetProfit(row);

            return [
                row.date,
                formatNumberOnly(row.revenue),
                revenueDeltaText,
                formatNumberOnly(netProfit), // 순수익
                row.req.toLocaleString(),
                row.res.toLocaleString(),
                fillRate,
                row.imp.toLocaleString(),
                row.clk.toLocaleString(),
                ctr,
                formatNumberOnly((row.revenue / Math.max(row.imp, 1)) * 1000)
            ];
        });

    }, [sortedData, baseSorted, calculateNetProfit]);

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <TableActionsMenu
                  tableRef={tableRef}
                  allRowsData={allRowsDataForExcel}
                  filename={filename}
                  sheetName="전체현황"
                />
            </Box>
            <TableContainer
                sx={{
                    overflowX: 'auto',
                    mt: 2
                }}
            >
              <Table ref={tableRef}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      <TableSortLabel
                        active
                        direction={sortDirection}
                        onClick={toggleSort}
                      >
                        날짜
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>총 매출</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>매출 증감률</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>순수익</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Request</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Response</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Fill Rate</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Impression</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Click</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>CTR</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>eCPM</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                    {/* 합계 행 */}
                    <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                        <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>합계</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                            {formatCurrency(totals.totalRevenue)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>-</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                            {formatCurrency(totals.totalProfit)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                            {totals.totalReq.toLocaleString()}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                            {totals.totalRes.toLocaleString()}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                {totals.avgFillRate}%
                                <Tooltip
                                    title={
                                    <span style={{ whiteSpace: 'pre-line', display: 'block', textAlign: 'center' }}>
                                        {"조회한 기간의 평균입니다\n* (노출수 / 요청수)"}
                                    </span>
                                    }
                                    arrow
                                >
                                    <InfoOutlinedIcon sx={{ fontSize: 14, color: '#666', cursor: 'help' }} />
                                </Tooltip>
                            </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                            {totals.totalImp.toLocaleString()}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                            {totals.totalClk.toLocaleString()}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                {totals.avgCTR}%
                                <Tooltip title="조회한 기간의 평균입니다" arrow>
                                    <InfoOutlinedIcon sx={{ fontSize: 14, color: '#666', cursor: 'help' }} />
                                </Tooltip>
                            </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                {totals.avgECPM}
                                <Tooltip title="조회한 기간의 평균입니다" arrow>
                                    <InfoOutlinedIcon sx={{ fontSize: 14, color: '#666', cursor: 'help' }} />
                                </Tooltip>
                            </Box>
                        </TableCell>
                    </TableRow>
                    {paged.map((row) => {
                        const revenueDelta = calculateRevenueDelta(row);
                        const fillRate = calculateFillRate(row.req, row.imp);
                        const ctr = calculateCTR(row.imp, row.clk);
                        const ecpm = calculateECPM(row.revenue, row.imp);
                        const netProfit = calculateNetProfit(row);

                        return (
                            <TableRow key={row.date}>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.date}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(row.revenue)}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{revenueDelta}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(netProfit)}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{row.req.toLocaleString()}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{row.res.toLocaleString()}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fillRate}%</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{row.imp.toLocaleString()}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{row.clk.toLocaleString()}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{ctr}%</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{ecpm}</TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={sortedData.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[rowsPerPage]}
            />
        </Box>
    );
  }

